import { Link as LinkBase } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ButtonSize, VariantColor, VariantStyle } from './Button.component';

const disabled = css`
  pointer-events: none;
  background-color: ${({ theme }) => theme.colors['neutral-30']};
  border-color: ${({ theme }) => theme.colors['neutral-30']};
  color: #abacaf;
`;

const yellow = css`
  background-color: ${({ theme }) => theme.colors['tertiary-60']};
  border-color: ${({ theme }) => theme.colors['tertiary-60']};
  color: ${({ theme }) => theme.colors['primary-60']};

  &:hover:not(:active) {
    background-color: ${({ theme }) => theme.colors['tertiary-50']};
  }
`;

const outlinedYellow = css`
  background-color: transparent;
  border-color: ${({ theme }) => theme.colors['tertiary-60']};
  color: ${({ theme }) => theme.colors['tertiary-60']};

  &:hover:not(:active) {
    background-color: ${({ theme }) => theme.colors['tertiary-20']};
  }
`;

const blue = css`
  background-color: ${({ theme }) => theme.colors['primary-60']};
  border-color: ${({ theme }) => theme.colors['primary-60']};
  color: ${({ theme }) => theme.colors['neutral-0']};

  &:hover:not(:active) {
    background-color: ${({ theme }) => theme.colors['primary-50']};
  }
`;

const outlinedBlue = css`
  background-color: transparent;
  border-color: ${({ theme }) => theme.colors['primary-60']};
  color: ${({ theme }) => theme.colors['primary-60']};

  &:hover:not(:active) {
    background-color: ${({ theme }) => theme.colors['secondary-20']};
  }
`;

const caution = css`
  background-color: ${({ theme }) => theme.colors.warning};
  border-color: ${({ theme }) => theme.colors.warning};
  color: ${({ theme }) => theme.colors['neutral-0']};

  &:hover:not(:active) {
    background-color: #fd7c74;
  }
`;

const outlinedCaution = css`
  background-color: transparent;
  border-color: ${({ theme }) => theme.colors.warning};
  color: ${({ theme }) => theme.colors.warning};

  &:hover:not(:active) {
    background-color: #fceeee;
  }
`;

const gray = css`
  background-color: ${({ theme }) => theme.colors['neutral-80']};
  border-color: ${({ theme }) => theme.colors['neutral-80']};
  color: ${({ theme }) => theme.colors['neutral-0']};

  &:hover:not(:active) {
    background-color: #fd7c74;
  }
`;

const outlinedGray = css`
  background-color: transparent;
  border-color: ${({ theme }) => theme.colors['neutral-80']};
  color: ${({ theme }) => theme.colors['neutral-80']};

  &:hover:not(:active) {
    background-color: ${({ theme }) => theme.colors['neutral-20']};
  }
`;

const buttonVariantStyles: {
  [color in VariantColor]: { [style in VariantStyle]: ReturnType<typeof css> };
} = {
  yellow: {
    filled: yellow,
    outline: outlinedYellow,
  },
  blue: {
    filled: blue,
    outline: outlinedBlue,
  },
  red: {
    filled: caution,
    outline: outlinedCaution,
  },
  gray: {
    filled: gray,
    outline: outlinedGray,
  },
};

const buttonCss = css<{
  $variantColor: VariantColor;
  $variantStyle: VariantStyle;
  $size: ButtonSize;
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 999px;
  font-weight: 500;
  font-size: ${({ $size }) => ($size === 'mini' ? '12px' : $size === 'small' ? '13px' : $size === 'large' ? '16px' : '14px')};
  line-height: 1.4;
  transition: background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1);
  border-width: 1px;
  min-height: ${({ $size }) => ($size === 'mini' ? '30px' : $size === 'small' ? '36px' : $size === 'large' ? '56px' : '48px')};
  padding: ${({ $size }) => ($size === 'mini' ? '6px 10px' : $size === 'small' ? '10px 12px' : $size === 'large' ? '16px 24px' : '14px 16px')};

  &:hover:not(:active):not(:disabled) {
    box-shadow: inset 999px 999px 0px rgba(255, 255, 255, 0.1);
  }
  ${({ $variantColor, $variantStyle }) => buttonVariantStyles[$variantColor][$variantStyle]}

  &:disabled {
    ${disabled}
  }

  &:active {
    outline-color: ${({ theme }) => theme.colors['secondary-60']};
    outline-offset: 0px;
    outline-style: solid;
    outline-width: 3px;
  }
`;

export const Button = styled.button`
  ${buttonCss}
`;

export const Link = styled(LinkBase)`
  ${buttonCss}
`;
